import React, { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import { Image, View } from 'react-native';
import PresentationView from './components/presentation/PresentationView';
import ProjectPresentationView from './components/projectpresentation/ProjectPresentationView';
import InvestBoxView from './components/investbox/InvestBoxView';
import AdvantagesView from './components/advantages/AdvantagesView';
import QuestionsView from './components/questions/QuestionsView';
import FooterView from './components/footer/FooterView';
import DocumentsView from './components/documents/DocumentsView';
import NavigationBarView from './components/navigationbar/NavigationBarView';
import LocalisationView from './components/localisation/LocalisationView';
import ImagesPresentationView from './components/imagespresentation/ImagesPresentationView';
import InvestCtaView from './components/investcta/InvestCtaView';
import LeaderView from './components/leader/LeaderView';
import PreviousProjectsView from './components/previousprojects/PreviousProjectsView';
import CalendarView from './components/calendar/CalendarView';
import FinanceSummaryView from './components/financesummary/FinanceSummaryView';
import WarningView from './components/warning/WarningView';
import VideosView from './components/videos/VideosView';
import ProjectsService from "../../../Proxy/ProjectsService";
import Project from "../../../Common/DTO/Project";
import { styles } from "./DetailsViewStyle";
import HeaderView from "./components/header/HeaderView";
import Sticky from "react-stickynode";
import Helpers from "../../../Common/Helpers/AmountHelpers";
import SafetyDescriptionView from "./components/safetydescription/SafetyDescriptionView";
import { useWindowSizeContext } from "../../../Common/contexts/WindowSizeContext";
import { ProjectOwner } from "../../../Common/DTO/ProjectOwner";
import UsersService from '../../../Proxy/UsersService';
import User from '../../../Common/DTO/User';
import { TranslationView } from '@levelapp/softfabric-ui';
import DocumentMeta from 'react-document-meta';
import { getCorrectTranslation, translate } from '../../../Common/Utils/translation.utils';
import { AboutView } from './components/about/AboutView';

interface DetailsViewProps {
  code: string;
}

export const DetailsView = ({ code }: DetailsViewProps) => {

  const { width, height, isMobile } = useWindowSizeContext();
  const [project, setProject] = useState<Project | undefined>(undefined);
  const [isProjectOpened, setIsProjectOpened] = useState<boolean>(false);

  const [showGeneral, setShowGeneral] = useState<boolean>(true);
  const [showLocalisation, setShowLocalisation] = useState<boolean>(true);
  const [showTimeline, setShowTimeline] = useState<boolean>(true);
  const [showLeader, setShowLeader] = useState<boolean>(true);
  const [showFinance, setShowFinance] = useState<boolean>(true);
  const [showDocuments, setShowDocuments] = useState<boolean>(true);

  const stickyTopOffset = 5;

  const projectService = new ProjectsService();

  const generalSectionName = 'general';

  const presentationOffsetBottom: number = 48;

  const localisationSectionName = 'localisation';

  const [localisationImages, setLocalisationImages] = useState<any[]>([]);
  const [address, setAddress] = useState<string>("");
  const [projectLocalisationText, setProjectLocalisationText] = useState<string>("");

  const [projectPresentationImages, setProjectPresentationImages] = useState<any[]>([]);

  const timelineSectionName = 'timeline';

  const leaderSectionName = 'leader';
  const leadersSectionName = 'menu.leaders';

  const safetySectionName = 'menu.safety';

  const [organigramImgUrl, setOrganigramImgUrl] = useState<string>("");
  const [projectOwners, setProjectOwners] = useState<ProjectOwner[]>([]);

  const financeSectionName = 'finance';

  const documentsSectionName = 'documents';

  const [financeSummaryProperties, setFinanceSummaryProperties] = useState<any[]>([]);

  const [sectionsShownedNames, setSectionsShownedNames] = useState<string[]>([]);

  const userService = new UsersService();

  function removeSectionShownedNames(sectionShownedName: string, sectionsArray: string[]): string[] {
    return sectionsArray.filter(name => name !== sectionShownedName);
  }

  function hideGeneral() {
    setShowGeneral(false);
  }

  function hideLocalisation() {
    setShowLocalisation(false);
  }

  function hideTimeline() {
    setShowTimeline(false);
  }

  function hideLeader() {
    setShowLeader(false);
  }

  function hideFinance() {
    setShowFinance(false);
  }

  function hideDocuments() {
    setShowDocuments(false);
  }

  const calculateSizeFactor = useCallback(() => {
    if (height > 900) return 1;
    if (height > 750) return 0.75;
    if (height > 650) return 0.65;
    if (height > 550) return 0.55;
    return 0.4;
  }, [height]);

  const sizeFactor: MutableRefObject<number> = useRef(calculateSizeFactor());

  useEffect(() => {
    if (code === 'rwdm') {
      if (typeof window !== 'undefined') {
        const lang = Helpers.getCurrentLanguage();
        const suffix = lang === 'NL' ? '/nl' : (lang === 'EN' ? '/en' : '');
        window.location.replace('https://rwdm-funding.be' + suffix);
      }
      return;
    }
    if (!project) {
      fetchSetProject();
      return;
    }

    // Modification of META
    //document.title = `${project.projectName} - ${project.annualInterestRate}% sur ${project.duration} an | Investir dès ${project.minimumInvest}€ | BeeBonds`;


    const projectPresentationImagesTmp = project.projectImages.filter(img => img.type === "GENERAL");

    setProjectPresentationImages(projectPresentationImagesTmp);

    const addressTmp = Helpers.getPropertyValue("ADRESS", project.projectPropertyValues);
    setAddress(addressTmp);
    const projectLocalisationTextTmp = Helpers.getCorrectTranslation(project.projectLocalisationText);
    setProjectLocalisationText(projectLocalisationTextTmp);
    const localisationImagesTmp = project.projectImages.filter((x: any) => x.type === 'LOCALISATION');
    setLocalisationImages(localisationImagesTmp);

    const globalProjectProperties: any[] = project.projectPropertyValues.filter(x => x.projectPropertyKey.projectPropertyType.key === "GLOBAL");

    const projectOwnersTmp = project.projectOwners;

    const leaderSectionNameTmp = projectOwnersTmp.length > 1 ? leadersSectionName : leaderSectionName;

    let shownSections = [generalSectionName, localisationSectionName, timelineSectionName, leaderSectionNameTmp, safetySectionName, financeSectionName, documentsSectionName];

    if (globalProjectProperties.length === 0) {
      hideGeneral();
      shownSections = removeSectionShownedNames(generalSectionName, shownSections);
    }

    if(!projectLocalisationTextTmp || !localisationImagesTmp) {
       hideLocalisation();
      shownSections = removeSectionShownedNames(localisationSectionName, shownSections);
    }

    if (projectLocalisationTextTmp && localisationImagesTmp && projectLocalisationTextTmp.length === 0 && localisationImagesTmp.length === 0 && addressTmp.length === 0) {
      hideLocalisation();
      shownSections = removeSectionShownedNames(localisationSectionName, shownSections);
    }

    if (project?.projectCalendars.length === 0) {
      hideTimeline();
      shownSections = removeSectionShownedNames(timelineSectionName, shownSections);
    }

    const organigramImgUrlTmp = Helpers.getPropertyValue("ORGANIGRAM", project.projectPropertyValues) ?? "";

    setOrganigramImgUrl(organigramImgUrlTmp);
    setProjectOwners(projectOwnersTmp);

    if (organigramImgUrlTmp.length === 0 && projectOwnersTmp.length === 0 && !Helpers.getPropertyValue("LEADER_DESCRIPTION", project.projectPropertyValues)) {
      hideLeader();
      shownSections = removeSectionShownedNames(leaderSectionName, shownSections);
    }

    const financeSummaryPropertiesTmp = project.projectPropertyValues.filter(prop => prop.projectPropertyKey.projectPropertyType.key === "FINANCE_SUMMARY_TAB");

    setFinanceSummaryProperties(financeSummaryPropertiesTmp);

    if (financeSummaryPropertiesTmp.length === 0) {
      hideFinance();
      shownSections = removeSectionShownedNames(financeSectionName, shownSections);
    }

    if (project.projectType.title === "FINANCED") {
      hideDocuments();
      shownSections = removeSectionShownedNames(documentsSectionName, shownSections);
    }

    if (project.code == "yoga2") {
      shownSections = removeSectionShownedNames(safetySectionName, shownSections);
    }

    setSectionsShownedNames(shownSections);

    sizeFactor.current = calculateSizeFactor();

    var logginStatus = false;
    var clientNumber = "";
    var userType = "";
    userService.getAll((userFound: User) => {
      logginStatus = true;
      clientNumber = userFound.clientNumber;
      userType = (userFound.investments.length > 0) ? "investor" : "basic";
    }, () => { });

    (window as any).dataLayer.push({
      'logginStatus': logginStatus,
      'userType': userType,
      'userId': clientNumber,
      'pageType': 'Project',
      'projectName': project.code
    });

  }, [project, width, calculateSizeFactor]);

  function fetchSetProject() {
    projectService.getByCode(code, (project: Project) => {
      setProject(project);
      setIsProjectOpened(project ? new Date(project.openingDate) < new Date() : false);
    });
  }

  const meta = {
    title: project
      ? `${project.projectName} - ${project.annualInterestRate}% ${translate('on')} ${project.duration} ${translate('years')} | ${translate('investFrom')} ${project.minimumTicket}€ | BeeBonds`
      : '',
    description: project
      ? getCorrectTranslation(project.projectHeader)
      : ''
  };

  return (
    <DocumentMeta {...meta}>
      <View>
        <HeaderView />
        <View style={[styles.page]}>
          {project &&
            <>
              <View>
                <div id="presentation">
                  <PresentationView project={project} />
                </div>

                <View style={{ zIndex: 50 }}>
                  <View style={isMobile ? [styles.oneColumn, { flexDirection: 'column-reverse', width: "100%" }] : styles.columns}>
                    <View style={[isMobile ? styles.oneColumn : [styles.column], { zIndex: 15 }]}>

                      <View style={{ height: presentationOffsetBottom }} />
                      <div id="navigation" style={{ zIndex: 20 }}>
                        {isMobile
                          ? (
                            <NavigationBarView sectionsShownedNames={sectionsShownedNames} />
                          ) : (
                            <Sticky top={stickyTopOffset} bottomBoundary="#documents">
                              <NavigationBarView sectionsShownedNames={sectionsShownedNames} />
                            </Sticky>
                          )
                        }
                      </div>

                      {showGeneral && (
                        <div id="general">
                          <ProjectPresentationView project={project} />
                        </div>
                      )}
                      <ImagesPresentationView images={projectPresentationImages} />
                    </View>
                    <div id="investBox" style={{ width: "100%" }}>
                      {isMobile
                        ? (
                          <>
                            <View style={{ height: presentationOffsetBottom }} />
                            <View style={[styles.oneColumn, { justifyContent: 'center', alignItems: 'center' }]}>
                              <InvestBoxView project={project} investBoxSizeFactor={sizeFactor.current} isProjectOpened={isProjectOpened} setIsProjectOpened={setIsProjectOpened} />
                            </View>
                          </>
                        ) : (
                          <View style={[styles.column, { width: '100%', marginRight: 0 }]}>
                            <View style={{ height: presentationOffsetBottom }} />
                            <Sticky top={stickyTopOffset} bottomBoundary="#documents">
                              <InvestBoxView project={project} investBoxSizeFactor={sizeFactor.current} isProjectOpened={isProjectOpened} setIsProjectOpened={setIsProjectOpened} />
                            </Sticky>
                          </View>
                        )
                      }
                    </div>
                  </View>
                </View>
                <View style={{ height: 128 }} />
                <View style={[styles.columns]}>
                  <View style={[isMobile ? [styles.oneColumn, { justifyContent: 'center', alignItems: 'center' }] : styles.column]}>
                    <AdvantagesView projectBenefits={project.projectBenefits} project={project} />
                    {project.code == "bod" &&
                      <>
                        <TranslationView style={{ fontSize: 22, color: '#535353', fontFamily: 'Montserrat', fontWeight: '700', marginTop: 14 }}>bod.what.title</TranslationView>
                        <TranslationView style={{ fontSize: 16, color: '#535353', fontFamily: 'Montserrat', fontWeight: '400', marginTop: 14 }}>bod.what.text</TranslationView>
                      </>
                    }
                    {project.code == "b5" &&
                      <>
                        <TranslationView style={{ fontSize: 22, color: '#535353', fontFamily: 'Montserrat', fontWeight: '700', marginTop: 14 }}>b5.what.title</TranslationView>
                        <TranslationView style={{ fontSize: 16, color: '#535353', fontFamily: 'Montserrat', fontWeight: '400', marginTop: 14 }}>b5.what.text</TranslationView>
                      </>
                    }
                    {project.code == "yustc" &&
                      <>
                        <TranslationView style={{ fontSize: 22, color: '#535353', fontFamily: 'Montserrat', fontWeight: '700', marginTop: 14 }}>b5.what.title</TranslationView>
                        <TranslationView style={{ fontSize: 16, color: '#535353', fontFamily: 'Montserrat', fontWeight: '400', marginTop: 14 }}>yustC.what.text</TranslationView>
                      </>
                    }
                     {project.code == "herm" &&
                      <>
                        <TranslationView style={{ fontSize: 22, color: '#535353', fontFamily: 'Montserrat', fontWeight: '700', marginTop: 14 }}>herm.about</TranslationView>
                        <TranslationView style={{ fontSize: 16, color: '#535353', fontFamily: 'Montserrat', fontWeight: '400', marginTop: 14 }}>herm.about.content</TranslationView>
                      </>
                    }
                    {project.projectAbout != null &&
                      <View style={{ marginTop: 24 }}>
                        <AboutView projectAbout={project.projectAbout} />
                      </View>
                    } 
                  </View>
                  {!isMobile && (
                    <View style={[styles.columnRight, { zIndex: -10 }]}>
                      <Image source={require('./../../../Common/assets/advantagesImage.png')} style={{ height: 583, width: 744 }} />
                    </View>
                  )}
                </View>
                <View style={isMobile ? styles.oneColumn : styles.column}>
                  <VideosView properties={project.projectPropertyValues} />
                  {showLocalisation && (
                    <div id="localisation">
                      <LocalisationView address={address} projectLocalisationText={projectLocalisationText} localisationImages={localisationImages} />
                    </div>
                  )}

                  {showTimeline && (
                    <div id="timeline">
                      <CalendarView calendars={project.projectCalendars} />
                    </div>
                  )}
                  {showLeader && (
                    <div id={projectOwners.length > 1 ? leadersSectionName : leaderSectionName}>
                      <LeaderView project={project} organigramImgUrl={organigramImgUrl} projectOwners={projectOwners} />
                    </div>
                  )}
                  <PreviousProjectsView previousProjects={project.previousProjects} />
                  <div id="menu.safety">
                    <SafetyDescriptionView project={project} />
                  </div>
                  {showFinance && (
                    <div id="finance">
                      <FinanceSummaryView project={project} financeSummaryProperties={financeSummaryProperties} />
                    </div>
                  )}

                  <View style={{ height: 64 }} />
                  <QuestionsView projectQuestions={project.projectQuestions} />

                  {showDocuments ? (
                    <div id="documents">
                      <DocumentsView projectDocuments={project.projectDocuments} documentsLegalInfoText={project.documentsLegalInfoText} />
                    </div>
                  ) : (
                    <div id="documents" />
                  )}

                  <View style={{ height: presentationOffsetBottom }} />
                </View>
              </View>
            </>
          }
        </View>
        {project &&
          <View>
            <div id="investCta">
              {isProjectOpened && project.projectTypeId != 3 && (
                <InvestCtaView project={project} />
              )}
            </div>
            <View style={styles.page}>
              <WarningView text={project.aboutLegalInfoText} />
            </View>
          </View>
        }
        <FooterView />
      </View>
    </DocumentMeta>

  );
}

export default DetailsView;